import { createSlice } from '@reduxjs/toolkit'

import {
  getAllTournaments,
  createTournament,
  joinTournament,
  leaveTournament,
  submitMatchResult,
  fetchBracketData,
  switchTeamInMatch,
  teamScoreModalOpen,
  teamScoreModalClose,
  setTeamScore,
  matchWinnerModalOpen,
  matchWinnerModalClose,
  setMatchWinner,
  updateTournamentStatus,
  startGroupPlay,
  swapTeamsBetweenGroups,
  moveTeamToGroup,
  getTournamentById,
  deleteTournament,
  retryTournamentDeletion,
  getPendingSubRequests,
  approveSubRequest,
  denySubRequest,
  requestSubFromPool,
} from './actions'
import { State } from './types'

const initialState: State = {
  isLoading: false,
  transactionHash: '',
  message: '',
  isCreateTournament: false,
  tournaments: [],
  tournament: {
    _id: '',
    avatar: {
      url: '',
    },
    cover_photo: {
      url: '',
    },
    tournament_name: '',
    tournament_description: '',
    tournament_status: '',
    tournament_sponsor: '',
    location: '',
    game: '',
    teams: [],
    play_format: 'single',
    number_of_teams: 0,
    prize_pool: 0,
    bracket: '',
    date: {
      start: '',
      end: '',
    },
    createdAt: '',
    updatedAt: '',
    max_player_size: 0,
    bracketData: [],
    winner: {},
    runner_up: {},
    transactionHash: '',
    prize_positions: [],
  },
  tournamentStatus: 'coming',
  newTournament: null,
  newTournamentMessage: '',
  joinedTournaments: [],
  joinSuccess: false,
  leaveSuccess: false,
  deleteMessage: '',
  error: null,
  submitMatchResultStatus: 'idle',
  bracketDataState: {
    loading: false,
    error: null,
    // ... other properties
  },
  teamScoreModal: {
    isOpen: false,
  },
  matchWinnerModal: {
    isOpen: false,
  },
  // Sub request management state
  pendingSubRequests: [],
  subPoolCount: 0,
  regionSubSuggestions: {},
  subRequestLoading: false,
  subRequestError: null,
  // Captain selection state
}

const tournamentSlice = createSlice({
  name: 'tournament',
  initialState,
  reducers: {
    messageCleanUp: (state) => {
      state.message = ''
    },
    newTournamentCleanUp: (state) => {
      state.newTournament = null
    },
    newTournamentMessageCleanUp: (state) => {
      state.newTournamentMessage = ''
    },
    transactionHashCleanUp: (state) => {
      state.transactionHash = ''
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTournaments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getAllTournaments.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.tournaments = payload
      })
      .addCase(getAllTournaments.rejected, (state, action) => {
        if (action.payload) {
          state.error = action.payload.errors
        } else {
          state.error = action.error.message
        }
      })
      .addCase(createTournament.pending, (state) => {
        state.isCreateTournament = true
        state.error = null
      })
      .addCase(createTournament.fulfilled, (state, { payload }) => {
        state.isCreateTournament = false
        state.newTournament = payload
        // if new tournament status is same as the current status, add it to the list
        if (
          state.tournaments[0] &&
          state.tournaments[0].tournament_status === payload.tournament_status
        ) {
          state.tournaments = [payload, ...state.tournaments]
        } else {
          state.tournaments = [payload, ...state.tournaments]
        }
        state.newTournamentMessage = 'Tournament created successfully'

        // state.tournaments = [{ ...payload }, ...state.tournaments]
      })
      .addCase(createTournament.rejected, (state, action) => {
        state.isCreateTournament = false
        if (action.payload) {
          state.error = action.payload.errors || action.payload.message
        } else {
          state.error = action.error.message
        }
      })
      // .addCase(joinTournament.fulfilled, (state, action) => {
      //   state.joinSuccess = false
      //   state.tournament == action.payload.updatedTournament
      //   console.log(
      //     'action.payload.tournament',
      //     action.payload.updatedTournament,
      //   )

      //   // if (action.payload.tournaments) {
      //   //   state.tournaments = action.payload.tournaments
      //   // } else {
      //   //   state.joinSuccess = false
      //   // }
      //   // state.tournaments = action.payload.tournaments
      // })
      // .addCase(joinTournament.rejected, (state) => {
      //   state.joinSuccess = false
      // })
      .addCase(joinTournament.fulfilled, (state, action) => {
        state.joinSuccess = false
        state.tournament = action.payload.updatedTournament
      })
      .addCase(joinTournament.rejected, (state, action: any) => {
        state.joinSuccess = false

        state.message = action.payload.message
      })
      .addCase(leaveTournament.fulfilled, (state, action: any) => {
        state.leaveSuccess = false
        state.tournament = action.payload.tournament
      })
      .addCase(leaveTournament.rejected, (state) => {
        state.leaveSuccess = false
      })
      .addCase(fetchBracketData.fulfilled, (state, action) => {
        const { tournamentId, bracketData } = action.payload
        if (typeof tournamentId === 'string') {
          const tournamentIndex = state.tournaments.findIndex(
            (t) => t._id === tournamentId,
          )
          if (tournamentIndex !== -1) {
            state.tournaments[tournamentIndex].bracketData = bracketData
          }
        }
      })
      .addCase(submitMatchResult.pending, (state) => {
        state.isLoading = true
        state.submitMatchResultStatus = 'pending'
      })
      .addCase(submitMatchResult.fulfilled, (state, action) => {
        state.isLoading = false
        state.submitMatchResultStatus = 'fulfilled'
        state.error = null // Reset error state after successful submission

        const { updatedTournamentData } = action.payload
        if (updatedTournamentData && updatedTournamentData._id) {
          const tournamentIndex = state.tournaments.findIndex(
            (t) => t._id === updatedTournamentData._id,
          )
          if (tournamentIndex !== -1) {
            state.tournaments[tournamentIndex] = updatedTournamentData
          }
        }
      })
      .addCase(submitMatchResult.rejected, (state, action) => {
        state.isLoading = false
        state.submitMatchResultStatus = 'rejected'
        state.error = String(action.payload) || 'Failed to submit match result'
      })
      .addCase(switchTeamInMatch.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(switchTeamInMatch.fulfilled, (state, action) => {
        state.isLoading = false
        state.error = null
        const { tournamentId, updatedBracket } = action.payload
        if (updatedBracket) {
          // Update the tournaments array
          if (state.tournaments) {
            const tournamentIndex = state.tournaments.findIndex(
              (t: any) => t._id === tournamentId
            )
            if (tournamentIndex !== -1) {
              state.tournaments[tournamentIndex] = {
                ...state.tournaments[tournamentIndex],
                bracketData: updatedBracket.bracketData,
                losersBracketData: updatedBracket.losersBracketData,
                rounds: updatedBracket.rounds,
                losersRounds: updatedBracket.losersRounds,
              }
            }
          }
          // Also update state.tournament (the singular object the bracket UI reads from)
          if (state.tournament && state.tournament._id === tournamentId) {
            state.tournament = {
              ...state.tournament,
              bracketData: updatedBracket.bracketData,
              losersBracketData: updatedBracket.losersBracketData,
              rounds: updatedBracket.rounds,
              losersRounds: updatedBracket.losersRounds,
            }
          }
        }
      })
      .addCase(switchTeamInMatch.rejected, (state, action: any) => {
        state.isLoading = false
        state.error = action.payload?.message || 'Failed to swap teams'
      })
      .addCase(teamScoreModalOpen, (state, { payload }) => {
        state.teamScoreModal = { ...payload, fullfiled: undefined }
      })
      .addCase(teamScoreModalClose, (state) => {
        state.teamScoreModal = {
          pending: false,
          isOpen: false,
          fullfiled: undefined,
        }
      })
      .addCase(setTeamScore.pending, (state) => {
        state.teamScoreModal.pending = true
      })
      .addCase(setTeamScore.rejected, (state, action) => {
        state.teamScoreModal.pending = false
        state.teamScoreModal.fullfiled = false
        console.error('setTeamScore rejected:', action.payload || action.error)
      })
      .addCase(setTeamScore.fulfilled, (state, { payload }) => {
        state.teamScoreModal.pending = false
        state.teamScoreModal.fullfiled = true
        if (payload.updatedTournament) {
          state.tournament = payload.updatedTournament
        }
      })
      .addCase(matchWinnerModalOpen, (state, { payload }) => {
        state.matchWinnerModal = { ...payload, fullfiled: undefined }
      })
      .addCase(matchWinnerModalClose, (state) => {
        state.matchWinnerModal = {
          pending: false,
          isOpen: false,
          fullfiled: undefined,
        }
      })
      .addCase(setMatchWinner.pending, (state) => {
        state.matchWinnerModal.pending = true
      })
      .addCase(setMatchWinner.rejected, (state) => {
        state.matchWinnerModal.pending = false
        state.matchWinnerModal.fullfiled = false
      })
      .addCase(setMatchWinner.fulfilled, (state, { payload }) => {
        state.matchWinnerModal.pending = false
        state.matchWinnerModal.fullfiled = true
        // state.tournaments = payload.tournaments
        state.tournament = payload.updatedTournament
      })
      // .addCase(updateTournamentStatus.pending, (state) => {
      //   state.isLoading = true
      // })
      // .addCase(updateTournamentStatus.fulfilled, (state, { payload }) => {
      //   state.isLoading = false
      //   state.tournament = payload.updatedTournament
      //   // console.log('payload', payload)

      //   if (payload.message == 'Please Select winner') {
      //     state.message = 'Please Select winner'
      //   } else if (payload.updatedTournament.tournament_status == 'end') {
      //     // console.log("Tournament has been ended");
      //     state.message = 'Tournament has been ended'
      //   } else if (payload.updatedTournament.tournament_status == 'progress') {
      //     state.message = 'Tournament has been started'
      //   }
      // })
      .addCase(updateTournamentStatus.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.tournament = payload.updatedTournament

        if (payload.updatedTournament?.play_format === 'captain_pick' && payload.draftStatus &&
          payload.updatedTournament?.tournament_status !== 'end' &&
          payload.updatedTournament?.tournament_status !== 'ending') {
          // Draft start: the caller (TournamentOverview) branches on draftStatus
          // and opens the captain modal / shows its own toast.
        } else if (
          payload.message ===
          'Number of teams should be greater than list payouts.'
        ) {
          state.message = 'Number of teams should be greater than list payouts.'
        } else if (payload.message === 'Please Select winner') {
          state.message = 'Please Select winner'
        } else if (
          payload.updatedTournament &&
          payload.updatedTournament.tournament_status === 'ending'
        ) {
          // Tournament is ending - USDC payouts processing in background
          state.message = 'Tournament ending - processing USDC payouts'
        } else if (
          payload.updatedTournament &&
          payload.updatedTournament.tournament_status === 'end'
        ) {
          // Check if updatedTournament is defined before accessing tournament_status
          state.message = 'Tournament has been ended'
          if (payload.updatedTournament.transactionHash) {
            state.transactionHash = payload.updatedTournament.transactionHash
          }
          if (payload.updatedTournament.payoutTransactionHash) {
            state.transactionHash = payload.updatedTournament.payoutTransactionHash
          }
        } else if (
          payload.updatedTournament &&
          payload.updatedTournament.tournament_status === 'progress'
        ) {
          state.message = 'Tournament has been started'
        }
      })
      .addCase(updateTournamentStatus.rejected, (state, action: any) => {
        state.isLoading = false
        console.log('action', action)

        // Handle both payload errors and network errors safely
        state.message = action.payload?.message || action.error?.message || 'Failed to update tournament status'
      })
      // Start Group Play cases
      .addCase(startGroupPlay.pending, (state) => {
        state.isLoading = true
      })
      .addCase(startGroupPlay.fulfilled, (state, { payload }) => {
        state.isLoading = false
        // Update tournament with the new data including groups
        if (payload.updatedTournament) {
          state.tournament = payload.updatedTournament
        }
        state.message = 'Group play started successfully'
      })
      .addCase(startGroupPlay.rejected, (state, action: any) => {
        state.isLoading = false
        state.error = action.payload?.message || 'Failed to start group play'
        state.message = action.payload?.message
      })
      // Swap teams between groups cases
      .addCase(swapTeamsBetweenGroups.fulfilled, (state, { payload }) => {
        // Update tournament groups with the swapped data
        if (state.tournament && payload.groups) {
          state.tournament.groups = payload.groups
        }
        state.message = payload.message || 'Teams swapped successfully'
      })
      .addCase(swapTeamsBetweenGroups.rejected, (state, action: any) => {
        state.error = action.payload?.message || 'Failed to swap teams'
        state.message = action.payload?.message
      })
      // Move team to group cases
      .addCase(moveTeamToGroup.fulfilled, (state, { payload }) => {
        if (state.tournament && payload.groups) {
          state.tournament.groups = payload.groups
        }
        state.message = payload.message || 'Team moved successfully'
      })
      .addCase(moveTeamToGroup.rejected, (state, action: any) => {
        state.error = action.payload?.message || 'Failed to move team'
        state.message = action.payload?.message
      })
      .addCase(getTournamentById.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getTournamentById.fulfilled, (state, { payload }) => {
        state.isLoading = false
        state.tournament = payload
      })
      .addCase(getTournamentById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      // Sub request management cases
      .addCase(getPendingSubRequests.pending, (state) => {
        state.subRequestLoading = true
        state.subRequestError = null
      })
      .addCase(getPendingSubRequests.fulfilled, (state, action) => {
        state.subRequestLoading = false
        state.pendingSubRequests = action.payload.pendingRequests || []
        state.subPoolCount = action.payload.subPoolCount || 0
        state.regionSubSuggestions = action.payload.regionSubSuggestions || {}
      })
      .addCase(getPendingSubRequests.rejected, (state, action) => {
        state.subRequestLoading = false
        const payload = action.payload as any
        state.subRequestError = typeof payload === 'string'
          ? payload
          : payload?.msg || payload?.message || 'Failed to fetch pending sub requests'
      })
      .addCase(approveSubRequest.pending, (state) => {
        state.subRequestLoading = true
      })
      .addCase(approveSubRequest.fulfilled, (state, action) => {
        state.subRequestLoading = false
        // Remove the approved request from pending list
        state.pendingSubRequests = state.pendingSubRequests.filter(
          req => req.id !== action.meta.arg.requestId
        )
      })
      .addCase(approveSubRequest.rejected, (state, action) => {
        state.subRequestLoading = false
        const payload = action.payload as any
        state.subRequestError = typeof payload === 'string'
          ? payload
          : payload?.msg || payload?.message || 'Failed to approve sub request'
      })
      .addCase(denySubRequest.pending, (state) => {
        state.subRequestLoading = true
      })
      .addCase(denySubRequest.fulfilled, (state, action) => {
        state.subRequestLoading = false
        // Remove the denied request from pending list
        state.pendingSubRequests = state.pendingSubRequests.filter(
          req => req.id !== action.meta.arg.requestId
        )
      })
      .addCase(denySubRequest.rejected, (state, action) => {
        state.subRequestLoading = false
        const payload = action.payload as any
        state.subRequestError = typeof payload === 'string'
          ? payload
          : payload?.msg || payload?.message || 'Failed to deny sub request'
      })
      .addCase(requestSubFromPool.pending, (state) => {
        state.subRequestLoading = true
      })
      .addCase(requestSubFromPool.fulfilled, (state) => {
        state.subRequestLoading = false
      })
      .addCase(requestSubFromPool.rejected, (state, action) => {
        state.subRequestLoading = false
        const payload = action.payload as any
        state.subRequestError = typeof payload === 'string'
          ? payload
          : payload?.msg || payload?.message || 'Failed to request substitute'
      })
      .addCase(deleteTournament.pending, (state) => {
        state.isLoading = true
      })
      .addCase(deleteTournament.fulfilled, (state, action) => {
        state.isLoading = false
        state.deleteMessage = action.payload.message
        //  exclude the deleted tournament from the list
        state.tournaments = state.tournaments.filter(
          (tournament) => tournament._id !== action.payload.tournamentId,
        )
      })
      .addCase(deleteTournament.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      // Retry tournament deletion
      .addCase(retryTournamentDeletion.pending, (state) => {
        state.isLoading = true
      })
      .addCase(retryTournamentDeletion.fulfilled, (state, action) => {
        state.isLoading = false
        state.deleteMessage = action.payload.message
        // Remove the tournament from the list after successful retry
        state.tournaments = state.tournaments.filter(
          (tournament) => tournament._id !== action.payload.tournamentId,
        )
        // Also clear the tournament detail if it matches
        if (state.tournament._id === action.payload.tournamentId) {
          state.tournament = initialState.tournament
        }
      })
      .addCase(retryTournamentDeletion.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
  },
})

export const { reducer, actions } = tournamentSlice

export const {
  messageCleanUp,
  newTournamentCleanUp,
  newTournamentMessageCleanUp,
  transactionHashCleanUp,
} = actions

export default tournamentSlice
